import { useCallback, useRef, useState } from 'react'
import { mediaLibraryApi } from '../../lib/api.js'
import { HttpError } from '../../lib/http.js'
import { useStore } from '../../store/index.js'
import type { UploadStatus } from './mediaLibraryFormat.js'

export type { UploadStatus }

export interface UploadTask {
  id:        string
  name:      string
  size:      number
  loaded:    number
  status:    UploadStatus
  /** Vitesse instantanée lissée, en octets/s (0 tant qu'inconnue). */
  speed:     number
  /** Secondes restantes estimées, ou null si non calculable. */
  etaSec:    number | null
  error?:    string
  destPath:  string
  destLabel: string
  /** Dépôt d'un nouveau fichier, ou remplacement d'un fichier existant. */
  kind:      'upload' | 'replace'
  /** Fichier écrasé (chemin relatif), pour un remplacement. */
  replaceTarget?: string
  startedAt?:  number
  finishedAt?: number
  /** Chemin relatif du fichier écrit, renvoyé par le serveur. */
  resultPath?: string
  /** Contrôle ffprobe à l'arrivée : le fichier a une durée lisible. */
  checkOk?:    boolean
  durationSec?: number | null
  /** Durée connue de la base avant un remplacement, pour signaler l'écart. */
  previousDurationSec?: number | null
}

interface QueuedFile {
  file: File; destPath: string; destLabel: string
  kind: 'upload' | 'replace'; replaceTarget?: string
}

let seq = 0
const nextId = () => `up_${Date.now()}_${seq++}`

/**
 * Une coupure réseau en cours d'envoi (accès distant bridé qui tranche la
 * connexion, ou passerelle 502/503/504) ne dit rien à l'opérateur si on affiche
 * le message brut. On la reformule en conseil actionnable : les gros fichiers
 * doivent passer par le réseau local, pas par l'accès distant.
 */
function isConnectionCut(err: unknown): boolean {
  return err instanceof HttpError && (err.status === 0 || err.status === 502 || err.status === 503 || err.status === 504)
}

function describeUploadError(err: unknown): string {
  if (isConnectionCut(err)) {
    return 'Connexion interrompue pendant l’envoi. Pour un fichier volumineux, téléversez depuis le réseau local plutôt que par l’accès distant.'
  }
  return err instanceof Error ? err.message : 'Échec de l’envoi'
}

/**
 * File d'attente d'envois, traitée séquentiellement : un seul média part à la
 * fois, ce qui donne une ETA lisible et évite de saturer le disque et le réseau
 * avec des fichiers de diffusion volumineux.
 */
export function useUploadQueue(onUploaded: (destPath: string, resultPath?: string) => void) {
  const [tasks, setTasks] = useState<UploadTask[]>([])

  const files    = useRef(new Map<string, QueuedFile>())
  const controls = useRef(new Map<string, AbortController>())
  const samples  = useRef(new Map<string, { t: number; loaded: number; speed: number }>())
  const queue    = useRef<string[]>([])
  const running  = useRef(false)
  const onDone   = useRef(onUploaded)
  onDone.current = onUploaded

  const patch = useCallback((id: string, changes: Partial<UploadTask>) => {
    setTasks(list => list.map(t => (t.id === id ? { ...t, ...changes } : t)))
  }, [])

  const pump = useCallback(async () => {
    if (running.current) return
    const id = queue.current.shift()
    if (!id) return
    const entry = files.current.get(id)
    if (!entry) { void pump(); return }

    running.current = true
    const ctrl = new AbortController()
    controls.current.set(id, ctrl)
    samples.current.set(id, { t: performance.now(), loaded: 0, speed: 0 })
    patch(id, { status: 'uploading', loaded: 0, speed: 0, etaSec: null, error: undefined, startedAt: Date.now(), finishedAt: undefined })

    const handlers = {
      signal: ctrl.signal,
      onProgress: (loaded: number, total: number) => {
        const prev = samples.current.get(id)
        const now  = performance.now()
        let speed  = prev?.speed ?? 0
        if (prev && now > prev.t) {
          const inst = ((loaded - prev.loaded) * 1000) / (now - prev.t)
          // Lissage exponentiel pour une vitesse et une ETA stables.
          speed = prev.speed ? prev.speed * 0.7 + inst * 0.3 : inst
        }
        samples.current.set(id, { t: now, loaded, speed })
        const etaSec = speed > 0 ? Math.max(0, Math.round((total - loaded) / speed)) : null
        patch(id, { loaded, speed, etaSec, status: 'uploading' })
      },
    }

    try {
      const result = entry.kind === 'replace' && entry.replaceTarget
        ? await mediaLibraryApi.replaceChunked(entry.file, entry.replaceTarget, handlers)
        : await mediaLibraryApi.uploadChunked(entry.file, entry.destPath || undefined, handlers)
      patch(id, {
        status: 'done', loaded: entry.file.size, speed: 0, etaSec: 0, finishedAt: Date.now(),
        resultPath: result.relativePath, checkOk: result.status === 'OK',
        durationSec: result.durationSec, previousDurationSec: result.previousDurationSec ?? null,
      })
      onDone.current(entry.destPath, result.relativePath)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        patch(id, { status: 'canceled', speed: 0, etaSec: null, finishedAt: Date.now() })
      } else {
        const message = describeUploadError(err)
        patch(id, { status: 'error', speed: 0, etaSec: null, error: message, finishedAt: Date.now() })
        // Une coupure réseau fait aussi tomber le WebSocket : l'app bascule sur
        // l'overlay de reconnexion et masque le suivi flottant. La notification,
        // rendue au niveau du shell, survit à l'overlay et reste le seul canal
        // par lequel l'opérateur voit pourquoi son envoi s'est arrêté.
        const label = entry.destLabel ? ` (${entry.destLabel})` : ''
        useStore.getState().pushToast(`« ${entry.file.name} »${label} — ${message}`, 'error', 0)
      }
    } finally {
      controls.current.delete(id)
      samples.current.delete(id)
      running.current = false
      void pump()
    }
  }, [patch])

  const add = useCallback((entries: QueuedFile[]) => {
    if (entries.length === 0) return
    const added: UploadTask[] = entries.map(entry => {
      const id = nextId()
      files.current.set(id, entry)
      queue.current.push(id)
      return {
        id, name: entry.file.name, size: entry.file.size, loaded: 0, status: 'pending', speed: 0, etaSec: null,
        destPath: entry.destPath, destLabel: entry.destLabel, kind: entry.kind, replaceTarget: entry.replaceTarget,
      }
    })
    // La file s'affiche dans son ordre d'arrivée ; le suivi trie lui-même par état.
    setTasks(prev => [...prev, ...added])
    void pump()
  }, [pump])

  const enqueue = useCallback((list: File[], destPath: string, destLabel: string) => {
    add(list.map(file => ({ file, destPath, destLabel, kind: 'upload' as const })))
  }, [add])

  /** Remplace `targetSubPath` par `file` (même nom et extension côté serveur). */
  const enqueueReplace = useCallback((file: File, targetSubPath: string, destPath: string, destLabel: string) => {
    add([{ file, destPath, destLabel, kind: 'replace', replaceTarget: targetSubPath }])
  }, [add])

  const cancel = useCallback((id: string) => {
    const ctrl = controls.current.get(id)
    if (ctrl) { ctrl.abort(); return }
    // Encore en attente : le retirer de la file sans l'avoir démarré.
    queue.current = queue.current.filter(q => q !== id)
    patch(id, { status: 'canceled', finishedAt: Date.now() })
  }, [patch])

  /** Vide la file et interrompt l'envoi en cours. */
  const cancelAll = useCallback(() => {
    const waiting = queue.current
    queue.current = []
    const now = Date.now()
    setTasks(list => list.map(t => (waiting.includes(t.id) ? { ...t, status: 'canceled', finishedAt: now } : t)))
    for (const ctrl of controls.current.values()) ctrl.abort()
  }, [])

  const retry = useCallback((id: string) => {
    if (!files.current.has(id)) return
    patch(id, { status: 'pending', loaded: 0, speed: 0, etaSec: null, error: undefined, finishedAt: undefined })
    queue.current.push(id)
    void pump()
  }, [patch, pump])

  const dismiss = useCallback((id: string) => {
    files.current.delete(id)
    setTasks(list => list.filter(t => t.id !== id))
  }, [])

  const clearFinished = useCallback(() => {
    setTasks(list => list.filter(t => {
      const finished = t.status === 'done' || t.status === 'canceled'
      if (finished) files.current.delete(t.id)
      return !finished
    }))
  }, [])

  return { tasks, enqueue, enqueueReplace, cancel, cancelAll, retry, dismiss, clearFinished }
}
